/**
 * Messages. Queries and answers in one thread.
 *
 * A question from the Board stops the statutory clock, which is office practice
 * rather than anything in the Act, so wherever a question is open this screen
 * says so plainly and says what restarts it.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { bi, type Bilingual } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";
import type { MessagesResponse, QueriesResponse, QueryView } from "../api-types.js";
import { formatDateTime, messageKindName } from "../format.js";
import { statusHref } from "../router.js";
import { Callout, EmptyState, ErrorNote, Loading, Panel, ProvenanceRow } from "../components/ui.js";

interface ThreadItem {
  id: string;
  at: string;
  fromBoard: boolean;
  /** Bilingual where the Board wrote it; plain where the applicant did. */
  kind: Bilingual | null;
  title: Bilingual | null;
  body: Bilingual | null;
  plainBody: string | null;
  citation: string | null;
}

export function Messages({ id }: { id: string }): JSX.Element {
  const { api } = useSession();
  const { t } = useLang();

  const [messages, setMessages] = useState<MessagesResponse | null>(null);
  const [queries, setQueries] = useState<QueriesResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [sending, setSending] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [m, q] = await Promise.all([
        api.get<MessagesResponse>(`/workflow/${id}/messages`),
        api.get<QueriesResponse>(`/workflow/${id}/queries`),
      ]);
      setMessages(m);
      setQueries(q);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [api, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const thread = useMemo<ThreadItem[]>(() => {
    const items: ThreadItem[] = [];
    for (const m of messages?.messages ?? []) {
      items.push({
        id: `m-${m.id}`,
        at: m.createdAt,
        fromBoard: true,
        kind: messageKindName(m.kind),
        title: m.subject,
        body: m.body,
        plainBody: null,
        citation: m.citation,
      });
    }
    for (const q of queries?.queries ?? []) {
      if (q.answer !== null && q.answeredAt !== null) {
        items.push({
          id: `a-${q.id}`,
          at: q.answeredAt,
          fromBoard: false,
          kind: null,
          title: null,
          body: null,
          plainBody: q.answer,
          citation: null,
        });
      }
    }
    return items.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  }, [messages, queries]);

  const answer = async (query: QueryView): Promise<void> => {
    const text = (answers[query.id] ?? "").trim();
    if (text.length === 0) return;
    setSending(query.id);
    setError(null);
    try {
      await api.post(`/workflow/${id}/queries/${query.id}/answer`, { answer: text });
      setAnswers((prev) => ({ ...prev, [query.id]: "" }));
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setSending(null);
    }
  };

  if (error && !messages) return <ErrorNote error={error} />;
  if (!messages || !queries) {
    return <Loading what={bi("Reading your messages", "നിങ്ങളുടെ സന്ദേശങ്ങൾ വായിക്കുന്നു")} />;
  }

  const open = queries.queries.filter((q) => q.open);

  return (
    <div className="stack">
      <div>
        <p className="eyebrow">{t(bi("Your application", "നിങ്ങളുടെ അപേക്ഷ"))}</p>
        <h1>{t(bi("Messages", "സന്ദേശങ്ങൾ"))}</h1>
        <p className="lede">
          {t(
            bi(
              "Everything the Board has written to you, and everything you have replied, in one place.",
              "ബോർഡ് നിങ്ങൾക്ക് എഴുതിയതും നിങ്ങൾ തിരികെ എഴുതിയതുമെല്ലാം ഒരിടത്ത്.",
            ),
          )}
        </p>
        <p className="no-print">
          <a href={statusHref(id)}>
            {t(bi("Back to the application", "അപേക്ഷയിലേക്ക് മടങ്ങുക"))}
          </a>
        </p>
      </div>

      {error ? <ErrorNote error={error} /> : null}

      {open.length > 0 ? (
        <Callout
          tone="warn"
          title={bi("The days are not being counted right now", "സമയഗണന നിർത്തിവച്ചിരിക്കുന്നു")}
          provenance={queries.pauseRule}
        >
          <p style={{ marginBottom: 0 }}>
            {t(
              bi(
                "While a question is waiting for your answer, the days are not counted. They start again on the day you answer. The sooner you answer, the sooner the Board's time starts again.",
                "ഒരു ചോദ്യം തീർപ്പാകാതെ കിടക്കുമ്പോൾ, നിയമപരമായ കാലാവധിയിലെ ദിവസങ്ങൾ കണക്കാക്കുന്നില്ല. നിങ്ങൾ മറുപടി നൽകുന്ന ദിവസം കണക്കാക്കൽ പുനരാരംഭിക്കും; നേരത്തെ മറുപടി നൽകുന്തോറും ബോർഡിന്റെ സമയം നേരത്തെ വീണ്ടും കണക്കാക്കിത്തുടങ്ങും.",
              ),
            )}
          </p>
        </Callout>
      ) : null}

      {open.map((q) => (
        <Panel key={q.id} title={bi("A question waiting for your answer", "നിങ്ങളുടെ മറുപടിക്കായി കാത്തിരിക്കുന്ന ചോദ്യം")}>
          <h3 style={{ marginTop: 0 }}>{t(q.subject)}</h3>
          <p className="muted">
            {t(
              bi(
                `Asked on ${formatDateTime(q.raisedAt, "en")}`,
                `${formatDateTime(q.raisedAt, "ml")}-ന് ചോദിച്ചു`,
              ),
            )}
          </p>
          <p className="message-body">{t(q.body)}</p>

          <div className="field no-print">
            <label className="field-label" htmlFor={`answer-${q.id}`}>
              {t(bi("Your answer", "നിങ്ങളുടെ മറുപടി"))}
            </label>
            <textarea
              id={`answer-${q.id}`}
              value={answers[q.id] ?? ""}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
            />
          </div>
          <button
            type="button"
            className="btn no-print"
            onClick={() => void answer(q)}
            disabled={sending === q.id || (answers[q.id] ?? "").trim().length === 0}
          >
            {sending === q.id ? <span className="spinner" aria-hidden="true" /> : null}
            {t(bi("Send my answer", "എന്റെ മറുപടി അയയ്ക്കുക"))}
          </button>
          <p className="muted" style={{ marginTop: "0.7rem", marginBottom: 0 }}>
            {t(
              bi(
                "When you send this answer, the Board's days start being counted again from today.",
                "ഇത് അയയ്ക്കുന്നതോടെ ഇന്ന് മുതൽ നിയമപരമായ കാലാവധിയുടെ കണക്കെടുപ്പ് പുനരാരംഭിക്കും.",
              ),
            )}
          </p>
        </Panel>
      ))}

      <section>
        <h2>{t(bi("The whole thread", "മുഴുവൻ സംഭാഷണം"))}</h2>
        {thread.length === 0 ? (
          <EmptyState
            title={bi("Nothing yet", "ഇതുവരെ ഒന്നുമില്ല")}
            body={bi(
              "The Board has not written to you about this application yet. Anything it sends you will appear here, including a question or a notice that it will visit your site.",
              "ഈ അപേക്ഷ സംബന്ധിച്ച് ബോർഡ് നിങ്ങൾക്ക് എഴുതിയിട്ടില്ല. സ്ഥലപരിശോധനാ അറിയിപ്പോ ചോദ്യമോ ഉൾപ്പെടെ ബോർഡ് എഴുതുന്നതെല്ലാം ഇവിടെ കാണാം.",
            )}
            provenance={messages.provenance}
          />
        ) : (
          <div className="thread">
            {thread.map((item) => (
              <ThreadCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </section>

      <div>
        <p className="muted">
          {t(
            bi(
              "The Rules require the Board to tell you its decision in writing. It is not optional.",
              "തീരുമാനം അപേക്ഷകനെ രേഖാമൂലം അറിയിക്കണം എന്നത് ചട്ടങ്ങളുടെ ആവശ്യമാണ്, മര്യാദയല്ല.",
            ),
          )}
        </p>
        <ProvenanceRow items={[messages.provenance, messages.note]} />
      </div>
    </div>
  );
}

function ThreadCard({ item }: { item: ThreadItem }): JSX.Element {
  const { lang, t } = useLang();

  return (
    <article className={["message", item.fromBoard ? "" : "message-from-you"].join(" ")}>
      <div className="message-head">
        <span className="message-who">
          {item.fromBoard
            ? t(bi("From the Board", "ബോർഡിൽ നിന്ന്"))
            : t(bi("From you", "നിങ്ങളിൽ നിന്ന്"))}
        </span>
        <span className="muted">{formatDateTime(item.at, lang)}</span>
      </div>
      {item.kind ? <p className="eyebrow">{t(item.kind)}</p> : null}
      {item.title ? <h3 style={{ marginTop: 0 }}>{t(item.title)}</h3> : null}
      <p className="message-body" style={{ marginBottom: 0 }}>
        {item.body ? t(item.body) : (item.plainBody ?? "")}
      </p>
      {item.citation ? (
        <p className="muted" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
          {t(bi("Under", "പ്രകാരം"))} {item.citation}
        </p>
      ) : null}
    </article>
  );
}
